import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useOrgProjects } from '../../lib/projects/useOrgProjects';
import { useRealtimeRowReload } from '../../hooks/useRealtimeRowReload';
import {
  fetchTypePipelines,
  stagesFor,
  type PipelineKey
} from '../../lib/workItemPipelines';
import type { Database } from '../../types/database';
import {
  type EpicOption,
  type LoadState,
  type WorkItemPropertyDefRow,
  type WorkItemPropertyValueRow
} from './workItemCanvasPropertyBarShared';

type WorkItemType = Database['public']['Enums']['work_item_type'];

export interface UseWorkItemCanvasPropertyBarArgs {
  workItemId: string;
  orgId: string;
  workItemType: WorkItemType;
  workItemStatus: string;
  workItemDueDate: string | null;
  workItemStartDate: string | null;
  workItemEndDate: string | null;
  parentWorkItemId: string | null;
  workItemProjectId: string | null;
  onStatusChange: (status: string) => void;
  onDueDateChange: (next: string | null) => void;
  onStartDateChange: (next: string | null) => void;
  onEndDateChange: (next: string | null) => void;
  onParentChange: (next: string | null) => void;
  onProjectChange: (next: string | null) => void;
  onError: (message: string) => void;
  onActivityChange?: () => void;
}

export function useWorkItemCanvasPropertyBar({
  workItemId,
  orgId,
  workItemType,
  workItemStatus,
  workItemDueDate,
  workItemStartDate,
  workItemEndDate,
  parentWorkItemId,
  workItemProjectId,
  onStatusChange,
  onDueDateChange,
  onStartDateChange,
  onEndDateChange,
  onParentChange,
  onProjectChange,
  onError,
  onActivityChange
}: UseWorkItemCanvasPropertyBarArgs) {
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [statusStages, setStatusStages] = useState<ReadonlyArray<string>>([]);
  const [epicOptions, setEpicOptions] = useState<EpicOption[]>([]);
  const [dateError, setDateError] = useState<string | null>(null);
  const { projects: projectOptions } = useOrgProjects(orgId);

  // Defer a live refresh while the user's own commit is in flight (the Realtime
  // event fires for our own write too) so it never re-fetches mid-write and
  // briefly stomps the optimistic value.
  const pendingCommitsRef = useRef(0);
  const refreshQueuedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const pipelineKey: PipelineKey =
      workItemType === 'epic'
        ? 'epic'
        : workItemType === 'subtask'
          ? 'subtask'
          : null;
    void (async () => {
      const res = await fetchTypePipelines([pipelineKey], orgId);
      if (cancelled) return;
      setStatusStages(stagesFor(res.byType, pipelineKey));
    })();
    return () => {
      cancelled = true;
    };
  }, [workItemType, orgId]);

  useEffect(() => {
    if (workItemType === 'epic' || workItemType === 'subtask') {
      setEpicOptions([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase
        .from('work_items')
        .select('id, name')
        .eq('org_id', orgId)
        .eq('type', 'epic')
        .order('name', { ascending: true });
      if (cancelled) return;
      if (error) {
        onError(error.message);
        return;
      }
      setEpicOptions(
        (data ?? []).map((r) => ({
          id: r.id as string,
          name: (r.name as string) ?? ''
        }))
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [workItemType, orgId, onError]);

  useEffect(() => {
    let cancelled = false;
    setLoad({ kind: 'loading' });
    void (async () => {
      const [defsRes, valsRes] = await Promise.all([
        supabase
          .from('work_item_property_defs')
          .select(
            'id, org_id, key, kind, label, description, default_value, position, type_id, visible_in_list, archived_at, created_at, updated_at, created_by'
          )
          .eq('org_id', orgId)
          .is('archived_at', null)
          .order('position', { ascending: true }),
        supabase
          .from('work_item_property_values')
          .select(
            'id, org_id, work_item_id, property_def_id, value, created_at, created_by, updated_at, updated_by'
          )
          .eq('work_item_id', workItemId)
      ]);
      if (cancelled) return;
      const err = defsRes.error?.message ?? valsRes.error?.message ?? null;
      if (err) {
        setLoad({ kind: 'error', message: err });
        return;
      }
      const values = new Map<string, WorkItemPropertyValueRow>();
      for (const row of (valsRes.data ?? []) as WorkItemPropertyValueRow[]) {
        values.set(row.property_def_id, row);
      }
      setLoad({
        kind: 'ready',
        defs: (defsRes.data ?? []) as WorkItemPropertyDefRow[],
        values
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [workItemId, orgId]);

  // Re-fetch ONLY this work item's property values and reconcile them into the
  // already-loaded bar (defs are unchanged by a value write). Used by the
  // Realtime subscription so an external/agent property edit appears inline
  // without a reopen. Reconciles to server truth; pendingCommits guards it from
  // fighting the user's own optimistic write.
  const refreshValues = useCallback(async () => {
    if (pendingCommitsRef.current > 0) {
      refreshQueuedRef.current = true;
      return;
    }
    const { data, error } = await supabase
      .from('work_item_property_values')
      .select(
        'id, org_id, work_item_id, property_def_id, value, created_at, created_by, updated_at, updated_by'
      )
      .eq('org_id', orgId)
      .eq('work_item_id', workItemId);
    if (error) {
      onError(error.message);
      return;
    }
    const values = new Map<string, WorkItemPropertyValueRow>();
    for (const row of (data ?? []) as WorkItemPropertyValueRow[]) {
      values.set(row.property_def_id, row);
    }
    setLoad((prev) => {
      if (prev.kind !== 'ready') return prev;
      return { ...prev, values };
    });
  }, [workItemId, orgId, onError]);

  // Live property refresh: subscribe to work_item_property_values rows for THIS
  // work item (FK filter work_item_id=eq.<workItemId>). Any external/agent write
  // (INSERT/UPDATE) triggers a debounced re-fetch — the same Supabase Realtime
  // mechanism that already refreshes the title/body for the open row.
  useRealtimeRowReload({
    table: 'work_item_property_values',
    filterColumn: 'work_item_id',
    rowId: workItemId || null,
    onChange: () => {
      void refreshValues();
    }
  });

  const commitValue = useCallback(
    async (defId: string, value: unknown) => {
      pendingCommitsRef.current += 1;
      const { data, error } = await supabase
        .from('work_item_property_values')
        .upsert(
          {
            work_item_id: workItemId,
            property_def_id: defId,
            org_id: orgId,
            value: value as never
          },
          { onConflict: 'work_item_id,property_def_id' }
        )
        .select(
          'id, org_id, work_item_id, property_def_id, value, created_at, created_by, updated_at, updated_by'
        )
        .single();
      pendingCommitsRef.current -= 1;
      if (pendingCommitsRef.current === 0 && refreshQueuedRef.current) {
        refreshQueuedRef.current = false;
        void refreshValues();
      }
      if (error) {
        onError(error.message);
        return;
      }
      setLoad((prev) => {
        if (prev.kind !== 'ready') return prev;
        const next = new Map(prev.values);
        next.set(defId, data as WorkItemPropertyValueRow);
        return { ...prev, values: next };
      });
    },
    [workItemId, orgId, onError, refreshValues]
  );

  const commitStatus = async (next: string) => {
    const previous = workItemStatus;
    onStatusChange(next);
    const { error } = await supabase.rpc('set_work_item_status', {
      p_work_item_id: workItemId,
      p_status: next
    });
    if (error) {
      onStatusChange(previous);
      onError(error.message);
      return;
    }
    onActivityChange?.();
  };

  const commitDue = async (next: string | null) => {
    const previous = workItemDueDate;
    onDueDateChange(next);
    const { error } = await supabase.rpc('set_work_item_dates', {
      p_work_item_id: workItemId,
      p_start_date: workItemStartDate,
      p_end_date: workItemEndDate,
      p_due_date: next
    });
    if (error) {
      onDueDateChange(previous);
      onError(error.message);
      return;
    }
    onActivityChange?.();
  };

  const commitStart = async (next: string | null) => {
    if (next && workItemEndDate && next > workItemEndDate) {
      setDateError('Start date cannot be after end date.');
      return;
    }
    setDateError(null);
    const previous = workItemStartDate;
    onStartDateChange(next);
    const { error } = await supabase.rpc('set_work_item_dates', {
      p_work_item_id: workItemId,
      p_start_date: next,
      p_end_date: workItemEndDate,
      p_due_date: workItemDueDate
    });
    if (error) {
      onStartDateChange(previous);
      onError(error.message);
      return;
    }
    onActivityChange?.();
  };

  const commitEnd = async (next: string | null) => {
    if (next && workItemStartDate && next < workItemStartDate) {
      setDateError('End date must be on or after start date.');
      return;
    }
    setDateError(null);
    const previous = workItemEndDate;
    onEndDateChange(next);
    const { error } = await supabase.rpc('set_work_item_dates', {
      p_work_item_id: workItemId,
      p_start_date: workItemStartDate,
      p_end_date: next,
      p_due_date: workItemDueDate
    });
    if (error) {
      onEndDateChange(previous);
      onError(error.message);
      return;
    }
    onActivityChange?.();
  };

  const commitParent = async (next: string | null) => {
    const previous = parentWorkItemId;
    onParentChange(next);
    const { error } = await supabase.rpc('set_work_item_parent', {
      p_work_item_id: workItemId,
      p_parent_id: next
    });
    if (error) {
      onParentChange(previous);
      onError(error.message);
      return;
    }
    onActivityChange?.();
  };

  const commitProject = async (next: string | null) => {
    const previous = workItemProjectId;
    onProjectChange(next);
    const { error } = await supabase.rpc('set_work_item_project', {
      p_work_item_id: workItemId,
      p_project_id: next
    });
    if (error) {
      onProjectChange(previous);
      onError(error.message);
      return;
    }
    onActivityChange?.();
  };

  return {
    load,
    statusStages,
    epicOptions,
    projectOptions,
    dateError,
    commitValue,
    commitStatus,
    commitDue,
    commitStart,
    commitEnd,
    commitParent,
    commitProject
  };
}
